import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import {
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiClipboard,
  FiDownload,
  FiUserCheck,
  FiUserPlus,
} from 'react-icons/fi';
import { ClioIntegrationCard } from '@/components/pro/ClioIntegrationCard';
import { apiFetch, apiJson, ApiError, extractApiErrorMessage } from '@/lib/api';
import { downloadProfessionalSectionExport } from '@/lib/pro-client-export';
import type {
  ProfessionalCodeBatchDetailResponse,
  ProfessionalCodeBatchListResponse,
  ProfessionalClientListResponse,
  ProfessionalInviteCode,
  ProfessionalInviteListResponse,
  ProfessionalInviteResponse,
} from '@/lib/types';

type CodeWithTrial = ProfessionalInviteCode & { trial_days: number };

const FONT =
  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

function coparentDisplayName(inv: ProfessionalInviteResponse): string {
  if (inv.coparent_name?.trim()) return inv.coparent_name.trim();
  return 'Co-parent';
}

function inviteLinkFor(code: string): string {
  return `${window.location.origin}/redeem/pro/${code}`;
}

function StatTile({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number | string;
  tone?: 'neutral' | 'highlight';
}) {
  return (
    <div
      className={`rounded-xl border px-4 py-3 ${
        tone === 'highlight'
          ? 'border-[#018A9D]/30 bg-[#018A9D]/5'
          : 'border-gray-200 bg-white'
      }`}
    >
      <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-500'>
        {label}
      </p>
      <p className='mt-0.5 text-2xl font-bold text-gray-900 tabular-nums'>
        {value}
      </p>
    </div>
  );
}

function CopyLinkButton({
  link,
  size = 'md',
}: {
  link: string;
  size?: 'sm' | 'md';
}) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // noop
    }
  };
  const sizeClass = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2.5 text-sm';
  return (
    <button
      type='button'
      onClick={onCopy}
      className={`inline-flex items-center gap-1.5 rounded-xl font-semibold transition-colors ${sizeClass} ${
        copied
          ? 'bg-emerald-600 text-white'
          : 'bg-[#018A9D] text-white hover:bg-[#017d8d]'
      }`}
    >
      {copied ? (
        <FiCheck className='h-4 w-4' />
      ) : (
        <FiClipboard className='h-4 w-4' />
      )}
      {copied ? 'Copied' : 'Copy invite link'}
    </button>
  );
}

function ProDashboardIndex() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const queryClient = useQueryClient();
  const [exportingId, setExportingId] = useState<number | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [reportDownloading, setReportDownloading] = useState<string | null>(null);
  const [showAllCodes, setShowAllCodes] = useState(false);

  const invitesQuery = useQuery({
    queryKey: ['professional-invites'],
    queryFn: () =>
      apiJson<ProfessionalInviteListResponse>(
        getToken,
        '/v1/professional/invites'
      ),
    enabled: isLoaded && !!isSignedIn,
  });

  const clientsQuery = useQuery({
    queryKey: ['professional-clients'],
    queryFn: () =>
      apiJson<ProfessionalClientListResponse>(
        getToken,
        '/v1/professional/clients'
      ),
    enabled: isLoaded && !!isSignedIn,
  });

  const codeBatchesQuery = useQuery({
    queryKey: ['professional-code-batches'],
    queryFn: () =>
      apiJson<ProfessionalCodeBatchListResponse>(
        getToken,
        '/v1/professional/codes/batches'
      ),
    enabled: isLoaded && !!isSignedIn,
  });

  const batchIds = useMemo(
    () => (codeBatchesQuery.data?.batches ?? []).map((b) => b.id),
    [codeBatchesQuery.data]
  );

  const batchDetailQueries = useQueries({
    queries: batchIds.map((id) => ({
      queryKey: ['professional-code-batch-detail', id],
      queryFn: () =>
        apiJson<ProfessionalCodeBatchDetailResponse>(
          getToken,
          `/v1/professional/codes/batches/${id}`
        ),
      enabled: isLoaded && !!isSignedIn,
    })),
  });

  const allCodes = useMemo<CodeWithTrial[]>(
    () =>
      batchDetailQueries.flatMap((q) =>
        q.data
          ? q.data.codes.map((c) => ({ ...c, trial_days: q.data!.batch.trial_days }))
          : []
      ),
    [batchDetailQueries]
  );
  const availableCodes = useMemo(
    () => allCodes.filter((c) => c.status === 'available'),
    [allCodes]
  );
  const redeemedCount = useMemo(
    () => allCodes.filter((c) => c.status === 'redeemed').length,
    [allCodes]
  );
  const featuredCode = availableCodes[0] ?? null;
  const totalCodes = allCodes.length;
  const trialDaysSet = useMemo(
    () => new Set(allCodes.map((c) => c.trial_days)),
    [allCodes]
  );
  const showPerCodeTrial = trialDaysSet.size > 1;

  const acceptMutation = useMutation({
    mutationFn: (inviteId: number) =>
      apiJson(getToken, `/v1/professional/invites/${inviteId}/accept`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['professional-invites'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['professional-clients'],
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (inviteId: number) =>
      apiJson(getToken, `/v1/professional/invites/${inviteId}/reject`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['professional-invites'],
      });
    },
  });

  const revokeCodeMutation = useMutation({
    mutationFn: (codeId: number) =>
      apiJson(getToken, `/v1/professional/codes/${codeId}/revoke`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['professional-code-batches'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['professional-code-batch-detail'],
      });
    },
  });

  const pendingInvites = useMemo(
    () =>
      (invitesQuery.data?.invites ?? []).filter((i) => i.status === 'pending'),
    [invitesQuery.data]
  );
  const activeClients = useMemo(
    () => clientsQuery.data?.clients ?? [],
    [clientsQuery.data]
  );

  const downloadExport = useCallback(
    async (inviteId: number) => {
      setExportError(null);
      setExportingId(inviteId);
      try {
        const res = await apiFetch(
          getToken,
          `/v1/professional/clients/${inviteId}/export`,
          {
            auth: true,
            headers: { Accept: 'application/zip,*/*' },
          }
        );
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new ApiError(`Export failed: ${res.status}`, res.status, text);
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `harbor-client-${inviteId}-export.zip`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e: unknown) {
        setExportError(extractApiErrorMessage(e, 'Could not download export.'));
      } finally {
        setExportingId(null);
      }
    },
    [getToken]
  );

  const downloadLawyerReport = useCallback(
    async (inviteId: number, section: string) => {
      setExportError(null);
      setReportDownloading(`${inviteId}:${section}`);
      try {
        await downloadProfessionalSectionExport(getToken, inviteId, section);
      } catch (e: unknown) {
        setExportError(extractApiErrorMessage(e, 'Could not download report.'));
      } finally {
        setReportDownloading(null);
      }
    },
    [getToken]
  );

  const codesLoading =
    codeBatchesQuery.isLoading || batchDetailQueries.some((q) => q.isLoading);
  const codesError = codeBatchesQuery.isError;

  return (
    <div className='max-w-3xl mx-auto px-6 py-10' style={{ fontFamily: FONT }}>
      <header className='mb-8'>
        <h1 className='text-3xl font-bold text-gray-900'>Welcome back</h1>
        <p className='text-gray-500 mt-1'>
          Invite clients, then view what they choose to share.
        </p>
      </header>

      <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8'>
        <StatTile
          label='Codes available'
          value={availableCodes.length}
          tone='highlight'
        />
        <StatTile label='Codes redeemed' value={redeemedCount} />
        <StatTile label='Active clients' value={activeClients.length} />
        <StatTile label='Pending' value={pendingInvites.length} />
      </div>

      {exportError ? (
        <div className='mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800'>
          {exportError}
        </div>
      ) : null}

      <ClioIntegrationCard />

      {!clientsQuery.isLoading &&
      !codesLoading &&
      activeClients.length === 0 &&
      pendingInvites.length === 0 ? (
        <section className='mb-8'>
          <div className='rounded-2xl border border-[#018A9D]/25 bg-[#018A9D]/5 p-5'>
            <h2 className='text-base font-semibold text-gray-900 mb-1'>
              Getting started
            </h2>
            <p className='text-sm text-gray-600 mb-4'>
              After sign-up, share an invite code so a client can start a trial
              and choose what to share with you.
            </p>
            <ol className='space-y-2.5 text-sm text-gray-700'>
              <li className='flex gap-2.5'>
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                    availableCodes.length > 0 || totalCodes > 0
                      ? 'bg-emerald-600 text-white'
                      : 'bg-[#018A9D] text-white'
                  }`}
                >
                  {availableCodes.length > 0 || totalCodes > 0 ? (
                    <FiCheck className='h-3 w-3' />
                  ) : (
                    '1'
                  )}
                </span>
                <span>
                  {totalCodes === 0
                    ? 'Create invite codes — contact Harbor admin if none appear after sign-up.'
                    : 'Invite codes ready — copy a link below and send it to your first client.'}
                </span>
              </li>
              <li className='flex gap-2.5'>
                <span className='mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[11px] font-bold text-gray-700'>
                  2
                </span>
                <span>
                  Client redeems the code (solo or paired household) and shares
                  calendar + expenses for counsel reports.
                </span>
              </li>
              <li className='flex gap-2.5'>
                <span className='mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[11px] font-bold text-gray-700'>
                  3
                </span>
                <span>
                  Download Parenting Time Compliance, Expense Dispute Summary,
                  or Hearing Prep Brief from their client card.
                </span>
              </li>
            </ol>
          </div>
        </section>
      ) : null}

      {pendingInvites.length > 0 ? (
        <section className='mb-8'>
          <div className='rounded-2xl border border-amber-200 bg-amber-50 p-5'>
            <h2 className='text-base font-semibold text-amber-900 mb-3'>
              {pendingInvites.length === 1
                ? '1 co-parent invited you to view their records'
                : `${pendingInvites.length} co-parents invited you to view their records`}
            </h2>
            <ul className='space-y-3'>
              {pendingInvites.map((inv) => (
                <li
                  key={inv.id}
                  className='rounded-xl bg-white border border-amber-200/50 p-4 flex flex-wrap items-center justify-between gap-3'
                >
                  <div>
                    <p className='font-semibold text-gray-900'>
                      {coparentDisplayName(inv)}
                    </p>
                  </div>
                  <div className='flex gap-2'>
                    <button
                      type='button'
                      disabled={acceptMutation.isPending}
                      onClick={() => acceptMutation.mutate(inv.id)}
                      className='rounded-xl bg-[#018A9D] hover:bg-[#017d8d] px-3.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50'
                    >
                      Accept
                    </button>
                    <button
                      type='button'
                      disabled={rejectMutation.isPending}
                      onClick={() => rejectMutation.mutate(inv.id)}
                      className='rounded-xl border border-gray-300 px-3.5 py-1.5 text-xs font-semibold text-gray-700 disabled:opacity-50'
                    >
                      Decline
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      <section className='mb-10'>
        <div className='flex items-center justify-between mb-3'>
          <h2 className='flex items-center gap-2 text-lg font-semibold text-gray-900'>
            <FiUserPlus className='h-5 w-5 text-[#018A9D]' />
            Invite a client
          </h2>
          {totalCodes > 0 ? (
            <p className='text-xs text-gray-500'>
              {availableCodes.length} of {totalCodes} codes available
              {!showPerCodeTrial && featuredCode
                ? ` · ${featuredCode.trial_days}-day trial each`
                : ''}
            </p>
          ) : null}
        </div>

        <div className='rounded-2xl border border-gray-200 bg-white shadow-sm p-5'>
          {codesLoading ? (
            <p className='text-sm text-gray-500'>Loading invite codes…</p>
          ) : codesError ? (
            <p className='text-sm text-red-700'>
              Could not load invite codes. Please refresh or contact Harbor
              admin.
            </p>
          ) : featuredCode ? (
            <>
              <p className='text-sm text-gray-600 mb-4'>
                Send this link to a client. They’ll get a{' '}
                <span className='font-semibold text-gray-900'>
                  {featuredCode.trial_days}-day free Harbor trial
                </span>{' '}
                and can choose what to share with you.
              </p>
              <div className='rounded-xl border border-gray-200 bg-gray-50 p-4 flex flex-wrap items-center gap-3 justify-between'>
                <div>
                  <p className='text-[11px] uppercase tracking-wide text-gray-500'>
                    Your next code
                  </p>
                  <p className='font-mono text-base font-semibold text-gray-900 mt-0.5'>
                    {featuredCode.code}
                  </p>
                  <p className='mt-1 inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800'>
                    {featuredCode.trial_days} days free
                  </p>
                </div>
                <CopyLinkButton link={inviteLinkFor(featuredCode.code)} />
              </div>
              {allCodes.length > 1 ? (
                <button
                  type='button'
                  onClick={() => setShowAllCodes((v) => !v)}
                  className='mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#018A9D]'
                >
                  {showAllCodes ? (
                    <>
                      <FiChevronUp className='h-3.5 w-3.5' /> Hide all codes
                    </>
                  ) : (
                    <>
                      <FiChevronDown className='h-3.5 w-3.5' />
                      Show all codes ({allCodes.length})
                    </>
                  )}
                </button>
              ) : null}

              {showAllCodes && allCodes.length > 0 ? (
                <ul className='mt-4 divide-y divide-gray-100 border-t border-gray-100'>
                  {allCodes.map((code) => (
                    <li
                      key={code.id}
                      className='py-3 flex flex-wrap items-center justify-between gap-2'
                    >
                      <div>
                        <p className='font-mono text-sm text-gray-900'>
                          {code.code}
                        </p>
                        <p className='text-xs text-gray-500'>
                          {code.status === 'redeemed' && code.redeemed_at
                            ? `Redeemed ${new Date(
                                code.redeemed_at
                              ).toLocaleDateString()}`
                            : code.status === 'available'
                              ? 'Available'
                              : code.status[0].toUpperCase() +
                                code.status.slice(1)}
                          {showPerCodeTrial
                            ? ` · ${code.trial_days}-day trial`
                            : ''}
                        </p>
                      </div>
                      <div className='flex gap-2'>
                        {code.status === 'available' ? (
                          <>
                            <CopyLinkButton
                              link={inviteLinkFor(code.code)}
                              size='sm'
                            />
                            <button
                              type='button'
                              onClick={() => revokeCodeMutation.mutate(code.id)}
                              disabled={revokeCodeMutation.isPending}
                              className='rounded-lg border border-red-300 px-2.5 py-1 text-xs font-semibold text-red-700 disabled:opacity-50'
                            >
                              Revoke
                            </button>
                          </>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : totalCodes === 0 ? (
            <p className='text-sm text-gray-500'>
              You don’t have any invite codes yet. Contact Harbor admin to
              receive codes.
            </p>
          ) : (
            <p className='text-sm text-gray-500'>
              All your codes have been used or revoked. Ask Harbor admin for
              more.
            </p>
          )}
        </div>
      </section>

      <section className='mb-10'>
        <h2 className='flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3'>
          <FiUserCheck className='h-5 w-5 text-[#018A9D]' />
          Your clients
          {activeClients.length > 0 ? (
            <span className='ml-1 text-xs font-normal text-gray-500'>
              ({activeClients.length})
            </span>
          ) : null}
        </h2>
        {clientsQuery.isLoading ? (
          <p className='text-sm text-gray-500'>Loading…</p>
        ) : activeClients.length === 0 ? (
          <div className='rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-center'>
            <p className='text-sm text-gray-600'>
              No clients yet. Send an invite code above — once a client redeems
              it and chooses what to share, they will appear here.
            </p>
          </div>
        ) : (
          <ul className='space-y-3'>
            {activeClients.map((inv) => (
              <li
                key={inv.id}
                className='rounded-2xl border border-gray-200 bg-white p-5 shadow-sm'
              >
                <div className='flex flex-wrap items-start justify-between gap-3'>
                  <div className='min-w-0'>
                    <p className='font-semibold text-gray-900'>
                      {coparentDisplayName(inv)}
                    </p>
                    <div className='mt-2 flex flex-wrap gap-1.5'>
                      {inv.share_chats ? (
                        <span className='rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-800'>
                          Chats
                        </span>
                      ) : null}
                      {inv.share_expenses ? (
                        <span className='rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800'>
                          Expenses
                        </span>
                      ) : null}
                      {inv.share_custody_calendar ? (
                        <span className='rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-800'>
                          Calendar
                        </span>
                      ) : null}
                      {inv.share_journal ? (
                        <span className='rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900'>
                          Journal
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    <Link
                      to='/pro/clients/$inviteId'
                      params={{ inviteId: String(inv.id) }}
                      className='inline-flex rounded-xl bg-[#018A9D] hover:bg-[#017d8d] px-3.5 py-1.5 text-xs font-semibold text-white'
                    >
                      View
                    </Link>
                    <button
                      type='button'
                      disabled={exportingId === inv.id}
                      onClick={() => downloadExport(inv.id)}
                      className='inline-flex items-center gap-1 rounded-xl border border-[#018A9D] px-3.5 py-1.5 text-xs font-semibold text-[#018A9D] disabled:opacity-50'
                    >
                      <FiDownload className='h-3.5 w-3.5' />
                      {exportingId === inv.id ? 'Preparing…' : 'Export all'}
                    </button>
                  </div>
                </div>
                {(inv.share_custody_calendar || inv.share_expenses) ? (
                  <div className='mt-3 flex flex-wrap gap-2 border-t border-gray-100 pt-3'>
                    {inv.share_custody_calendar ? (
                      <button
                        type='button'
                        disabled={reportDownloading !== null}
                        onClick={() => downloadLawyerReport(inv.id, 'exchange')}
                        className='inline-flex items-center gap-1 rounded-lg bg-[#018A9D]/10 px-2.5 py-1.5 text-[11px] font-semibold text-[#018A9D] disabled:opacity-50'
                      >
                        <FiDownload className='h-3 w-3' />
                        {reportDownloading === `${inv.id}:exchange`
                          ? 'Preparing…'
                          : 'Compliance Report'}
                      </button>
                    ) : null}
                    {inv.share_expenses ? (
                      <button
                        type='button'
                        disabled={reportDownloading !== null}
                        onClick={() =>
                          downloadLawyerReport(inv.id, 'expense_disputes')
                        }
                        className='inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-800 disabled:opacity-50'
                      >
                        <FiDownload className='h-3 w-3' />
                        {reportDownloading === `${inv.id}:expense_disputes`
                          ? 'Preparing…'
                          : 'Expense Dispute Summary'}
                      </button>
                    ) : null}
                    <button
                      type='button'
                      disabled={reportDownloading !== null}
                      onClick={() => downloadLawyerReport(inv.id, 'hearing_prep')}
                      className='inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold text-amber-900 disabled:opacity-50'
                    >
                      <FiDownload className='h-3 w-3' />
                      {reportDownloading === `${inv.id}:hearing_prep`
                        ? 'Preparing…'
                        : 'Hearing Prep Brief'}
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <Link
          to='/pro/directory'
          className='flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white p-4 hover:border-[#018A9D]/40 transition-colors'
        >
          <div>
            <p className='text-sm font-semibold text-gray-900'>
              Get listed in the Harbor directory
            </p>
            <p className='text-xs text-gray-500 mt-0.5'>
              Free listing — appear to co-parents browsing professionals.
            </p>
          </div>
          <span className='text-[#018A9D] text-sm font-semibold'>Set up →</span>
        </Link>
      </section>
    </div>
  );
}

export const Route = createFileRoute('/pro/')({
  component: ProDashboardIndex,
});
